import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';

import KosaService from '../../services/kosa-service';
import localizeTime from '../../services/localize-time';
import useErrorHandler from '../../services/error-handler';
import { StyledDialogTitle, StyledTableCell } from '../StyledComponents';
import { stateColor, stateStr } from '../../models/kosaState';
import KosaStateDialog from './KosaStateDialog';

//=================================================================================================
class Store {
    load = false;
    orderInfo = null;
    items = [];

    constructor() {
        makeAutoObservable(this);
    }

    async get(sn) {
        if (!sn) {
            this.items = [];
            return;
        }
        this.load = true;
        try {
            const answer = await KosaService.history(sn);
            this.orderInfo = answer.data.order;
            this.items = answer.data.history;
        } catch (e) {
            console.warn(e);
            throw new Error('Ошибка загрузки данных');
        } finally {
            this.load = false;
        }
    }
}

const store = new Store();

//=================================================================================================
const HistoryItem = ({ item }) => {
    return (
        <TableRow sx={{ backgroundColor: stateColor(item.state_id) }}>
            <TableCell>{localizeTime(item.time)}</TableCell>
            <TableCell>{stateStr(item.state_id)}</TableCell>
            <TableCell>{item.first_name + ' ' + item.last_name}</TableCell>
        </TableRow>
    );
};

//=================================================================================================
const History = observer(({ sn, onClose }) => {
    const navigate = useNavigate();
    const pushError = useErrorHandler();

    const [stateDialog, setStateDialog] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);

    useEffect(() => {
        if (sn) load();
    }, [sn]);

    const load = async () => {
        try {
            await store.get(sn);
        } catch (e) {
            pushError(e);
        }
    };

    const reload = () => {
        setStateDialog(null);
        load();
    };

    const onSetStateClick = () => {
        setMenuAnchor(null);
        if (!store.items.length) return;
        setStateDialog(store.items.at(0));
    };

    const renderKosaName = () => {
        let marking = '';
        if (store.items.length && store.items.at(0).marking.length) marking = ' - ' + store.items.at(0).marking;
        return (
            <DialogContent>
                <Stack direction="row" alignItems="center">
                    <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                        <MenuIcon />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                        <MenuItem onClick={onSetStateClick}>Изменить статус косы</MenuItem>
                    </Menu>
                    <Typography sx={{ textAlign: 'center' }}>
                        История косы <b>{sn}</b>
                        {marking}
                    </Typography>
                </Stack>
            </DialogContent>
        );
    };

    const renderOrderInfo = () => {
        if (!store.orderInfo) return null;
        return (
            <DialogContentText sx={{ textAlign: 'right', pr: 1 }}>
                Заказ номер:{' '}
                <Link
                    component="button"
                    onClick={() => {
                        navigate(`/orders/${store.orderInfo.order_id}`);
                    }}
                >
                    {store.orderInfo.number}
                </Link>
            </DialogContentText>
        );
    };

    const renderTable = () => {
        if (store.load) return <div>Загрузка...</div>;
        if (store.items.length === 0) return <div>Нет данных</div>;
        return (
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Время</StyledTableCell>
                            <StyledTableCell>Статус</StyledTableCell>
                            <StyledTableCell>Пользователь</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {store.items.map((item) => (
                            <HistoryItem key={item.time} item={item} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <>
            <Dialog open={Boolean(sn)} onClose={onClose}>
                <StyledDialogTitle>История косы {sn}</StyledDialogTitle>
                <DialogContent>
                    {renderKosaName()}
                    {renderOrderInfo()}
                    {renderTable()}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={onClose}>
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>
            <KosaStateDialog kosa={stateDialog} onApply={reload} onClose={() => setStateDialog(null)} />
        </>
    );
});

//=================================================================================================
const KosaHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const sn = +params.sn;

    const onClose = () => {
        const address = location.state ? location.state.backgroundLocation.pathname : '/kosas/' + sn;
        navigate(address, { state: { refresh: true }, replace: true });
    };

    return <History sn={sn} onClose={onClose} />;
};

export default KosaHistory;
